/**
 * 물음표 도움말 — 이름 옆의 작은 물음표. 마우스를 올리거나(키보드 초점·터치) 누르면 설명이 뜬다.
 *
 * **화면에는 꼭 필요한 글만 둔다.** 이름·값·단추로 할 일을 알 수 있으면 설명을 붙이지 않고,
 * "왜·언제·주의할 점" 처럼 처음 한 번만 읽으면 되는 말은 여기로 옮긴다. 늘 펼쳐 둔 설명은 화면을
 * 길게 만들고, 몇 번 보고 나면 아무도 읽지 않는다.
 *
 * 말풍선은 문서 맨 위 층(`document.body`)에 띄운다 — 모달·표·접히는 패널은 넘치는 것을 자르는데,
 * 그 안에 두면 말풍선이 잘린다. 자리는 물음표 아래가 기본이고, 아래가 모자라면 위로 올린다.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp } from 'lucide-react';

/** 말풍선과 물음표 사이, 그리고 창 가장자리와 띄우는 거리. */
const GAP = 6;
const EDGE = 8;

/** 말풍선 자리 — 물음표 아래(기본)인지 위인지, 그리고 왼쪽 위 좌표. */
export function placeBubble(
  anchor: { top: number; bottom: number; left: number; width: number },
  bubble: { width: number; height: number },
  viewport: { width: number; height: number },
): { top: number; left: number; side: 'below' | 'above' } {
  const below = anchor.bottom + GAP;
  const fitsBelow = below + bubble.height <= viewport.height - EDGE;
  const above = anchor.top - GAP - bubble.height;
  const side = fitsBelow || above < EDGE ? 'below' : 'above';
  const centered = anchor.left + anchor.width / 2 - bubble.width / 2;
  const left = Math.max(EDGE, Math.min(centered, viewport.width - bubble.width - EDGE));
  return { top: side === 'below' ? below : above, left, side };
}

interface Props {
  /** 설명. 한두 문장 — 길면 설명서로 보낸다. */
  text: ReactNode;
  /** 화면 낭독기가 읽는 단추 이름. 무엇에 대한 도움말인지 적는다(예: "채널 도움말"). */
  label?: string;
}

export function HelpTip({ text, label = '도움말' }: Props): ReactNode {
  const id = useId();
  const anchor = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  /** 눌러서 연 것인가 — 그때는 마우스가 떠나도 닫지 않는다(터치에는 '떠남' 이 없다). */
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setPinned(false);
    setPosition(null);
  }, []);

  useLayoutEffect(() => {
    if (!open || !anchor.current || !bubble.current) return;
    const a = anchor.current.getBoundingClientRect();
    const b = bubble.current.getBoundingClientRect();
    const next = placeBubble(
      { top: a.top, bottom: a.bottom, left: a.left, width: a.width },
      { width: b.width, height: b.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition({ top: next.top, left: next.left });
  }, [open, text]);

  /* 열려 있는 동안 — 바깥을 누르거나, 스크롤하거나, Esc 면 닫는다(자리가 어긋난 말풍선을 남기지 않는다). */
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent): void => {
      if (anchor.current?.contains(event.target as Node)) return;
      close();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="help-tip"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => {
          if (!pinned) close();
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={(event) => {
          // 창(모달) 안의 물음표에서 Esc 는 말풍선만 닫는다 — 창까지 닫지 않는다.
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            close();
          }
        }}
        onClick={(event) => {
          // 줄 전체가 눌리는 표·라벨 안에 있어도 그쪽 동작을 일으키지 않는다.
          event.preventDefault();
          event.stopPropagation();
          if (pinned) close();
          else {
            setOpen(true);
            setPinned(true);
          }
        }}
      >
        <CircleHelp aria-hidden="true" strokeWidth={2} />
      </button>
      {open
        ? createPortal(
            <div
              ref={bubble}
              id={id}
              role="tooltip"
              className="help-tip-bubble"
              style={position ? { top: position.top, left: position.left } : { top: -9999, left: -9999 }}
            >
              {text}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
